import { FC } from 'react';
import { Box, Button, Container, ContainerProps, Fab, FabProps, IconButton, Skeleton } from '@mui/material';
import Message, { MessageProps } from './Message';
import { To, useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import PageCrumbs from './PageCrumbs';

export interface Props extends ContainerProps {
  title?: string;
  description?: string;
  titleProps?: Omit<MessageProps, 'content'>;
  descriptionProps?: Omit<MessageProps, 'content'>;

  /**
   * @deprecaed Use `back` instead
   */
  goback?: boolean;
  back?: boolean | 'up' | 'previous';
  loading?: boolean;
  error?: string;
  crumbs?: boolean;
}

const PageContainer: FC<Props> = ({
  children,
  loading,
  error,
  crumbs,
  title,
  description,
  descriptionProps,
  titleProps,
  back,
  goback,
  ...props
}) => {
  const navigate = useNavigate();

  const innerBack = back || goback;

  const onClickBack = () => {
    let path: string | number;
    if (innerBack === 'up') {
      path = '..';
    } else {
      path = -1;
    }

    navigate(path as To);
  };

  if (loading) {
    return (
      <Container {...props}>
        <Skeleton variant='text' width={200} height={50} />
        <Skeleton variant='text' width={150} height={40} />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 4 }}>
          <Skeleton variant='rounded' width='100%' height={40} />
          <Skeleton variant='rounded' width='100%' height={40} />
          <Skeleton variant='rounded' width='100%' height={40} />
        </Box>
      </Container>
    );
  }

  return (
    <Container sx={{ position: 'relative', height: '100%' }} {...props}>
      {crumbs && <PageCrumbs />}
      <Box>
        {innerBack && (
          <IconButton sx={{ float: 'left' }} size='small' onClick={onClickBack}>
            <Icon icon='lets-icons:back' width='24' height='24' />
          </IconButton>
        )}
        {title && <Message content={title as MessageProps['content']} {...{ variant: 'h1', ...titleProps }} />}
        {description && (
          <Message content={description as MessageProps['content']} {...{ my: 1, ...descriptionProps }} />
        )}
      </Box>

      {error && (
        <Box sx={{ my: 2 }}>
          <Message content='error' />{' '}
          <Button onClick={onClickBack} size='small' variant='outlined' color='primary'>
            Go Back
          </Button>
        </Box>
      )}

      {!error && children}
    </Container>
  );
};

export default PageContainer;
